import * as THREE from 'three/webgpu';
import type {WorkerManager} from '../../../worker';
import type {SketchGroup} from '../../../mesh/sketch';
import type {BossRevolveSceneContour} from '../../../feature/edit/revolve';
import {RevolveRegionContourObject} from '../../../mesh/feature/revolve';
import {RevolveSketchRuntimeInputSerializer} from './RevolveSketchRuntimeInputSerializer';
import { addObject3DToParent } from '../../../utils/scene-object/addObject3DToParent';

/**
 * 旋转区域选择管理器构造参数。
 */
export interface RevolveRegionSelectionManagerOptions {
    /** 场景。 */
    scene: THREE.Scene;
    /** 当前相机读取器。 */
    getCamera: () => THREE.Camera | null;
    /** 当前容器读取器。 */
    getContainer: () => HTMLElement | null;
    /** 草图组解析器。 */
    resolveSketchGroup: (sketchId: string) => SketchGroup | null;
    /** Worker 管理器。 */
    workerManager: WorkerManager;
    /** 请求渲染。 */
    requestRender: () => void;
}

/**
 * 旋转区域选择管理器。
 *
 * 文件定位：
 * 1. 位于 cad-core Viewer 模块，负责相交闭合区域的显示、拾取、hover 与 selected 状态；
 * 2. 仅对外提供纯数据与 cad-core scene contour，不把 Object3D 暴露给 web-client；
 * 3. 统一管理区域 mesh 的创建和销毁，避免选择模式多次切换后泄漏资源。
 */
export class RevolveRegionSelectionManager {
    /** 构造参数。 */
    private readonly options: RevolveRegionSelectionManagerOptions;

    /** 草图运行时输入序列化器。 */
    private readonly serializer = new RevolveSketchRuntimeInputSerializer();

    /** 当前是否处于区域选择模式。 */
    private enabled = false;

    /** 当前激活草图 ID。 */
    private activeSketchId: string | null = null;

    /** 按草图缓存的区域数据。 */
    private readonly regionsBySketchId = new Map<string, SketchRegionPayload[]>();

    /** 按草图缓存的区域显示层。 */
    private readonly layerBySketchId = new Map<string, THREE.Group>();

    /** 当前 hover 区域 ID。 */
    private hoveredRegionId: string | null = null;

    /** 当前 selected 区域 ID。 */
    private selectedRegionIds = new Set<string>();

    /** 当前是否显示区域填充。 */
    private fillVisible = true;

    /**
     * 构造旋转区域选择管理器。
     * @param options 构造参数。
     */
    public constructor(options: RevolveRegionSelectionManagerOptions) {
        this.options = options;
    }

    /**
     * 开启指定草图的区域选择。
     * @param sketchId 草图 ID。
     */
    public async enable(sketchId: string): Promise<void> {
        this.enabled = true;
        this.activeSketchId = sketchId;
        this.hoveredRegionId = null;
        this.selectedRegionIds.clear();
        await this.refresh(sketchId);
        this.bindHoverListener();
        this.updateLayerVisibility();
        this.updateVisuals();
    }

    /**
     * 禁用区域选择并释放临时区域 mesh。
     */
    public disable(): void {
        this.enabled = false;
        this.activeSketchId = null;
        this.hoveredRegionId = null;
        this.selectedRegionIds.clear();
        this.fillVisible = true;
        this.unbindHoverListener();
        this.regionsBySketchId.clear();
        this.layerBySketchId.forEach((layer) => {
            this.clearLayer(layer);
            layer.parent?.remove(layer);
        });
        this.layerBySketchId.clear();
        this.options.requestRender();
    }

    /**
     * 刷新指定草图的区域。
     * @param sketchId 草图 ID。
     */
    public async refresh(sketchId: string): Promise<void> {
        const sketchGroup = this.options.resolveSketchGroup(sketchId);
        if (!sketchGroup) {
            this.regionsBySketchId.set(sketchId, []);
            return;
        }
        const payload = this.serializer.buildFindRegionsPayload(sketchGroup);
        if (!payload) {
            this.regionsBySketchId.set(sketchId, []);
            this.rebuildLayer(sketchGroup, []);
            return;
        }
        const response = await this.options.workerManager.findSketchRegions(payload);
        const regions = response.regions ?? [];
        this.regionsBySketchId.set(sketchId, regions);
        this.rebuildLayer(sketchGroup, regions);
        this.updateLayerVisibility();
        this.updateVisuals();
    }

    /**
     * 获取指定草图的区域。
     * @param sketchId 草图 ID。
     * @returns 区域集合。
     */
    public getRegions(sketchId: string): SketchRegionPayload[] {
        return this.regionsBySketchId.get(sketchId) ?? [];
    }

    /**
     * 构建当前草图运行时输入。
     * @param sketchId 草图 ID。
     * @returns Worker 载荷；无可用实体时返回 null。
     */
    public getRuntimeInput(sketchId: string): FindSketchRegionsPayload | null {
        const sketchGroup = this.options.resolveSketchGroup(sketchId);
        return sketchGroup ? this.serializer.buildFindRegionsPayload(sketchGroup) : null;
    }

    /**
     * 设置 hover 区域。
     * @param regionId 区域 ID。
     */
    public setHover(regionId: string | null): void {
        if (this.hoveredRegionId === regionId) {
            return;
        }
        this.hoveredRegionId = regionId;
        this.updateVisuals();
    }

    /**
     * 设置已选择区域。
     * @param regionIds 区域 ID。
     */
    public setSelections(regionIds: string[]): void {
        this.selectedRegionIds = new Set(regionIds.filter((regionId) => regionId.length > 0));
        this.updateVisuals();
    }

    /**
     * 设置区域填充是否可见。
     *
     * 设计说明：
     * 旋转预览体出现后，已选闭合区域的半透明填充会与预览体端面共面深度争抢；
     * 这里只关闭填充显示，保留区域 mesh 的拾取能力，避免破坏多轮廓选择流程。
     *
     * @param visible 是否显示区域填充。
     */
    public setFillVisible(visible: boolean): void {
        if (this.fillVisible === visible) {
            return;
        }
        this.fillVisible = visible;
        this.updateVisuals();
    }

    /**
     * 按对象 ID 解析区域轮廓。
     * @param objectId 对象 ID。
     * @returns 旋转场景轮廓。
     */
    public resolveContourFromObjectId(objectId: string): BossRevolveSceneContour | null {
        const regionObject = this.findRegionObject(objectId);
        return regionObject ? this.toSceneContour(regionObject) : null;
    }

    /**
     * 从鼠标事件拾取区域。
     * @param event 鼠标事件。
     * @returns 区域数据。
     */
    public pickRegionFromEvent(event: MouseEvent): SketchRegionPayload | null {
        const hit = this.pickRegionObjectFromEvent(event);
        return hit?.region ?? null;
    }

    /**
     * 释放所有资源。
     */
    public dispose(): void {
        this.disable();
    }

    /**
     * 绑定 hover 监听。
     */
    private bindHoverListener(): void {
        const container = this.options.getContainer();
        container?.removeEventListener('mousemove', this.handlePointerMove);
        container?.addEventListener('mousemove', this.handlePointerMove);
    }

    /**
     * 解绑 hover 监听。
     */
    private unbindHoverListener(): void {
        this.options.getContainer()?.removeEventListener('mousemove', this.handlePointerMove);
    }

    /**
     * 处理鼠标移动。
     * @param event 鼠标事件。
     */
    private readonly handlePointerMove = (event: MouseEvent): void => {
        if (!this.enabled) {
            return;
        }
        this.setHover(this.pickRegionFromEvent(event)?.id ?? null);
    };

    /**
     * 拾取区域对象。
     * @param event 鼠标事件。
     * @returns 区域对象。
     */
    private pickRegionObjectFromEvent(event: MouseEvent): RevolveRegionContourObject | null {
        if (!this.enabled || !this.activeSketchId) {
            return null;
        }
        const layer = this.layerBySketchId.get(this.activeSketchId);
        const camera = this.options.getCamera();
        const container = this.options.getContainer();
        if (!layer || !camera || !container) {
            return null;
        }
        const rect = container.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }
        const pointer = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1,
        );
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(pointer, camera);
        const hit = raycaster
            .intersectObjects(layer.children, false)
            .find((item) => item.object instanceof RevolveRegionContourObject);
        return hit?.object instanceof RevolveRegionContourObject ? hit.object : null;
    }

    /**
     * 重建草图区域显示层。
     * @param sketchGroup 草图组。
     * @param regions 区域集合。
     */
    private rebuildLayer(sketchGroup: SketchGroup, regions: SketchRegionPayload[]): void {
        const layer = this.getOrCreateLayer(sketchGroup);
        this.clearLayer(layer);
        regions.forEach((region) => {
            addObject3DToParent(new RevolveRegionContourObject(region), layer);
        });
        this.options.requestRender();
    }

    /**
     * 获取或创建区域显示层。
     * @param sketchGroup 草图组。
     * @returns 区域显示层。
     */
    private getOrCreateLayer(sketchGroup: SketchGroup): THREE.Group {
        const existing = this.layerBySketchId.get(sketchGroup.uuid);
        if (existing && existing.parent === sketchGroup) {
            return existing;
        }
        const layer = new THREE.Group();
        layer.name = `__revolve_region_layer_${sketchGroup.uuid}`;
        addObject3DToParent(layer, sketchGroup);
        this.layerBySketchId.set(sketchGroup.uuid, layer);
        return layer;
    }

    /**
     * 清理区域显示层。
     * @param layer 区域显示层。
     */
    private clearLayer(layer: THREE.Group): void {
        [...layer.children].forEach((child) => {
            layer.remove(child);
            if (child instanceof RevolveRegionContourObject) {
                child.dispose();
            }
        });
    }

    /**
     * 更新区域层可见性。
     */
    private updateLayerVisibility(): void {
        this.layerBySketchId.forEach((layer, sketchId) => {
            layer.visible = this.enabled && this.activeSketchId === sketchId;
        });
        this.options.requestRender();
    }

    /**
     * 更新全部区域显示状态。
     */
    private updateVisuals(): void {
        this.layerBySketchId.forEach((layer) => {
            layer.children.forEach((child) => {
                if (!(child instanceof RevolveRegionContourObject)) {
                    return;
                }
                child.updateVisualState({
                    hovered: this.hoveredRegionId === child.region.id,
                    selected: this.selectedRegionIds.has(child.region.id),
                    fillVisible: this.fillVisible,
                });
            });
        });
        this.options.requestRender();
    }

    /**
     * 按 ID 查找区域对象。
     * @param objectId 对象 ID。
     * @returns 区域对象。
     */
    private findRegionObject(objectId: string): RevolveRegionContourObject | null {
        for (const layer of this.layerBySketchId.values()) {
            const matched = layer.children.find((child) => (
                child instanceof RevolveRegionContourObject
                && (
                    child.uuid === objectId
                    || child.name === objectId
                    || child.region.id === objectId
                    || child.region.contourKey === objectId
                )
            ));
            if (matched instanceof RevolveRegionContourObject) {
                return matched;
            }
        }
        return null;
    }

    /**
     * 把区域对象转换成旋转场景轮廓。
     * @param regionObject 区域对象。
     * @returns 旋转场景轮廓。
     */
    private toSceneContour(regionObject: RevolveRegionContourObject): BossRevolveSceneContour {
        const region = regionObject.region;
        const geometry: ProfileRegionGeometry = {
            outer: region.outer.map((point) => [...point] as [number, number, number]),
            outerSegments: region.outerSegments?.map((segment) => structuredClone(segment)),
            holes: region.holes.map((hole) => hole.map((point) => [...point] as [number, number, number])),
            holeSegments: region.holeSegments?.map((segments) => segments.map((segment) => structuredClone(segment))),
            normal: [...region.normal] as [number, number, number],
            xAxis: [...region.xAxis] as [number, number, number],
            yAxis: [...region.yAxis] as [number, number, number],
        };
        return {
            id: region.id,
            sketchId: region.sketchId,
            contourKey: region.contourKey,
            geometry,
            basePoint: new THREE.Vector3(...region.center),
            normal: new THREE.Vector3(...region.normal).normalize(),
            sceneObject: regionObject,
            regionIdentity: structuredClone(region.identity),
        };
    }
}
